"use client";

import { c as _c } from "react/compiler-runtime";
import { useIsMobile } from "@/lib/hooks";
import { Breakpoint, useCurrentBreakpoint } from "@/lib/hooks/useBreakpoint";
import { useIsKeyPress } from "@/lib/useKeyPress";
import { useInView, useMotionValue } from "motion/react";
import { MouseEventHandler, useEffect, useId, useRef, useState } from "react";

/** Parked off-canvas until the first pointer move or scroll places it. */
const INITIAL_POSITION = {
  x: 0,
  y: -1000
} as const;
type FlashlightColor = "purple" | "purple-medium" | "purple-dark" | "yellow" | "white" | "black";
interface FlashlightContainerProps extends React.PropsWithChildren {
  /**
   * Additional CSS classes on the container
   */
  className?: string;
  /**
   * The text that is used to mask the flashlight
   */
  text1?: string;
  text2?: string;
  /**
   * The flashlights radius
   */
  radius?: number;
  /**
   * The flashlights color (accepts hex, rgb, and hsl)
   */
  color?: FlashlightColor;
  /**
   * The flashlights opacity (accepts values in the range 0-1)
   */
  opacity?: number;
}
export const FlashlightContainer: React.FC<FlashlightContainerProps> = t0 => {
  const $ = _c(70);
  const {
    children,
    text1: t1,
    text2: t2,
    className: t3,
    radius: t4,
    color: t5,
    opacity: t6
  } = t0;
  const text1 = t1 === undefined ? "MUUUH!" : t1;
  const text2 = t2 === undefined ? "MUUUH!" : t2;
  const className = t3 === undefined ? "" : t3;
  const _radius = t4 === undefined ? 300 : t4;
  const color = t5 === undefined ? "purple" : t5;
  const _opacity = t6 === undefined ? 0.5 : t6;
  const id = useId();
  const glowId = "flashlight-glow-" + id;
  const textMaskId = "flashlight-text-mask-" + id;
  const svgRef = useRef(null);
  const ref = useRef(null);
  const isShiftPressed = useIsKeyPress("Shift");
  const radius = (isShiftPressed ? 2 : 1) * _radius;
  const opacity = (isShiftPressed ? 1.5 : 1) * _opacity;
  const [isHovered, setIsHovered] = useState(false);
  const positionX = useMotionValue(INITIAL_POSITION.x);
  const positionY = useMotionValue(INITIAL_POSITION.y);
  const gradientRef = useRef(null);
  const glowRef = useRef(null);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      w: 0,
      h: 0
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const [dimensions, setDimensions] = useState(t7);
  const isInView = useInView(ref);
  const isMobile = useIsMobile();
  const breakpoint = useCurrentBreakpoint();
  let t8;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      yellow: "#ccff00",
      purple: "#9747ff",
      "purple-medium": "#8600ff",
      "purple-dark": "#53039b",
      black: "#000000",
      white: "#ffffff"
    };
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const colorMap = t8;
  const hexColor = colorMap[color];
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      "2xl": 300,
      xl: 250,
      lg: 200,
      md: 150,
      sm: 120,
      xs: 100
    };
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const fontSizeMap = t9;
  const fontSize = fontSizeMap[breakpoint] ?? 100;
  let t10;
  let t11;
  if ($[3] !== positionX || $[4] !== positionY || $[5] !== radius) {
    t10 = () => {
      const paintFlashlight = () => {
        const x = positionX.get();
        const y = positionY.get();
        gradientRef.current?.setAttribute("cx", String(x));
        gradientRef.current?.setAttribute("cy", String(y));
        glowRef.current?.setAttribute("x", String(x - radius));
        glowRef.current?.setAttribute("y", String(y - radius));
      };
      paintFlashlight();
      const unsubscribes = [positionX.on("change", paintFlashlight), positionY.on("change", paintFlashlight)];
      return () => unsubscribes.forEach(_temp);
    };
    t11 = [positionX, positionY, radius];
    $[3] = positionX;
    $[4] = positionY;
    $[5] = radius;
    $[6] = t10;
    $[7] = t11;
  } else {
    t10 = $[6];
    t11 = $[7];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[8] !== isMobile || $[9] !== positionX) {
    t12 = () => {
      const updateDimensions = () => {
        if (!svgRef.current) {
          return;
        }
        const {
          width,
          height
        } = svgRef.current.getBoundingClientRect();
        setDimensions({
          w: width,
          h: height
        });
        if (isMobile) {
          positionX.set(width * 0.5);
        }
      };
      updateDimensions();
      window.addEventListener("resize", updateDimensions);
      return () => window.removeEventListener("resize", updateDimensions);
    };
    t13 = [isMobile, positionX];
    $[8] = isMobile;
    $[9] = positionX;
    $[10] = t12;
    $[11] = t13;
  } else {
    t12 = $[10];
    t13 = $[11];
  }
  useEffect(t12, t13);
  let t14;
  if ($[12] !== isMobile || $[13] !== positionX || $[14] !== positionY) {
    t14 = e => {
      if (isMobile || !ref.current) {
        return;
      }
      const {
        left,
        top
      } = ref.current.getBoundingClientRect();
      positionX.set(e.clientX - left);
      positionY.set(e.clientY - top);
    };
    $[12] = isMobile;
    $[13] = positionX;
    $[14] = positionY;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const handleMouseMove = t14;
  let t15;
  let t16;
  if ($[16] !== isInView || $[17] !== isMobile || $[18] !== positionY) {
    t15 = () => {
      const element = ref.current;
      if (!isMobile || !isInView || !element) {
        return;
      }
      let animationFrameRequestId;
      const updateFlashlightPosition = () => {
        cancelAnimationFrame(animationFrameRequestId);
        animationFrameRequestId = requestAnimationFrame(() => {
          const {
            top: top_0
          } = element.getBoundingClientRect();
          positionY.set(window.scrollY - top_0);
        });
      };
      window.addEventListener("scroll", updateFlashlightPosition, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", updateFlashlightPosition);
        cancelAnimationFrame(animationFrameRequestId);
      };
    };
    t16 = [isMobile, isInView, positionY];
    $[16] = isInView;
    $[17] = isMobile;
    $[18] = positionY;
    $[19] = t15;
    $[20] = t16;
  } else {
    t15 = $[19];
    t16 = $[20];
  }
  useEffect(t15, t16);
  let t17;
  if ($[21] !== isMobile) {
    t17 = function fadeInDesktop() {
      if (isMobile) {
        return;
      }
      setIsHovered(true);
    };
    $[21] = isMobile;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const fadeInDesktop = t17;
  let t18;
  if ($[23] !== isMobile) {
    t18 = function fadeOutDesktop() {
      if (isMobile) {
        return;
      }
      setIsHovered(false);
    };
    $[23] = isMobile;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  const fadeOutDesktop = t18;
  const t19 = `relative size-full min-h-50 md:min-h-60 lg:min-h-80 xl:min-h-100 2xl:min-h-120 ${className}`;
  const t20 = `absolute size-full transition-opacity -z-1 ${isHovered || isMobile ? "opacity-100 duration-400" : "opacity-0 duration-800"}`;
  let t21;
  if ($[25] !== hexColor || $[26] !== opacity) {
    t21 = <stop offset="0%" stopColor={hexColor} stopOpacity={opacity} />;
    $[25] = hexColor;
    $[26] = opacity;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== hexColor) {
    t22 = <stop offset="100%" stopColor={hexColor} stopOpacity={0} />;
    $[28] = hexColor;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== glowId || $[31] !== radius || $[32] !== t21 || $[33] !== t22) {
    t23 = <radialGradient ref={gradientRef} id={glowId} cx={INITIAL_POSITION.x} cy={INITIAL_POSITION.y} r={radius} gradientUnits="userSpaceOnUse">{t21}{t22}</radialGradient>;
    $[30] = glowId;
    $[31] = radius;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  const t24 = dimensions.w * 0.5 - fontSize * 0.655;
  const t25 = dimensions.h * 0.5 - fontSize * 0.3;
  let t26;
  if ($[35] !== fontSize || $[36] !== t24 || $[37] !== t25 || $[38] !== text1) {
    t26 = <text x={t24} y={t25} dominantBaseline="middle" textAnchor="middle" fontSize={fontSize} fontWeight={900}>{text1}</text>;
    $[35] = fontSize;
    $[36] = t24;
    $[37] = t25;
    $[38] = text1;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  const t27 = dimensions.w * 0.5 + fontSize * 0.655;
  const t28 = dimensions.h * 0.5 + fontSize * 0.5;
  let t29;
  if ($[40] !== fontSize || $[41] !== t27 || $[42] !== t28 || $[43] !== text2) {
    t29 = <text x={t27} y={t28} dominantBaseline="middle" textAnchor="middle" fontSize={fontSize} fontWeight={900}>{text2}</text>;
    $[40] = fontSize;
    $[41] = t27;
    $[42] = t28;
    $[43] = text2;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t26 || $[46] !== t29 || $[47] !== textMaskId) {
    t30 = <clipPath id={textMaskId} clipPathUnits="userSpaceOnUse">{t26}{t29}</clipPath>;
    $[45] = t26;
    $[46] = t29;
    $[47] = textMaskId;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t23 || $[50] !== t30) {
    t31 = <defs>{t23}{t30}</defs>;
    $[49] = t23;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  const t32 = INITIAL_POSITION.x - radius;
  const t33 = INITIAL_POSITION.y - radius;
  const t34 = radius * 2;
  const t35 = radius * 2;
  const t36 = `url(#${glowId})`;
  const t37 = `url(#${textMaskId})`;
  let t38;
  if ($[52] !== t32 || $[53] !== t33 || $[54] !== t34 || $[55] !== t35 || $[56] !== t36 || $[57] !== t37) {
    t38 = <rect ref={glowRef} x={t32} y={t33} width={t34} height={t35} fill={t36} clipPath={t37} />;
    $[52] = t32;
    $[53] = t33;
    $[54] = t34;
    $[55] = t35;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
  } else {
    t38 = $[58];
  }
  let t39;
  if ($[59] !== t20 || $[60] !== t31 || $[61] !== t38) {
    t39 = <svg ref={svgRef} className={t20} aria-hidden={true}>{t31}{t38}</svg>;
    $[59] = t20;
    $[60] = t31;
    $[61] = t38;
    $[62] = t39;
  } else {
    t39 = $[62];
  }
  let t40;
  if ($[63] !== children || $[64] !== fadeInDesktop || $[65] !== fadeOutDesktop || $[66] !== handleMouseMove || $[67] !== t19 || $[68] !== t39) {
    t40 = <div ref={ref} onMouseEnter={fadeInDesktop} onMouseLeave={fadeOutDesktop} onMouseMove={handleMouseMove} className={t19}>{t39}{children}</div>;
    $[63] = children;
    $[64] = fadeInDesktop;
    $[65] = fadeOutDesktop;
    $[66] = handleMouseMove;
    $[67] = t19;
    $[68] = t39;
    $[69] = t40;
  } else {
    t40 = $[69];
  }
  return t40;
};
function _temp(unsubscribe) {
  return unsubscribe();
}